/**
 * What the assistant offers when it has nothing else to go on.
 *
 * These are deliberately the four things a vendor actually comes here to do,
 * phrased as the vendor would say them rather than as the system thinks of
 * them — "what changed this week", not "query the movement detector". Each one
 * is a real sentence the assistant can act on today with the tools it has.
 *
 * Kept short. A wall of suggestions reads as a menu the vendor has to study,
 * and the point of a text box is that they do not have to.
 */

export interface Suggestion {
    /** Stable id, used as the React key and in the i18n key. */
    readonly id: string;
    /** Which tool family this exercises — drives the icon. */
    readonly kind: 'analytics' | 'content' | 'catalogue' | 'design';
    readonly titleKey: string;
    readonly titleFallback: string;
    readonly hintKey: string;
    readonly hintFallback: string;
    /** The sentence dropped into the composer when it is clicked. */
    readonly promptKey: string;
    readonly promptFallback: string;
}

export const SUGGESTIONS: Suggestion[] = [
    {
        id: 'whats-changed',
        kind: 'analytics',
        titleKey: 'ASSISTANT.SUGGESTION.WHATS_CHANGED',
        titleFallback: 'What changed this week',
        hintKey: 'ASSISTANT.SUGGESTION.WHATS_CHANGED_HINT',
        hintFallback: 'Movements worth knowing about',
        promptKey: 'ASSISTANT.SUGGESTION.WHATS_CHANGED_PROMPT',
        promptFallback: 'What changed in my shop this week, and what should I do about it?',
    },
    {
        id: 'needs-content',
        kind: 'content',
        titleKey: 'ASSISTANT.SUGGESTION.NEEDS_CONTENT',
        titleFallback: 'Which products need images',
        hintKey: 'ASSISTANT.SUGGESTION.NEEDS_CONTENT_HINT',
        hintFallback: 'Popular products with nothing made for them',
        promptKey: 'ASSISTANT.SUGGESTION.NEEDS_CONTENT_PROMPT',
        promptFallback: 'Which of my products are getting attention but have no Content Studio images yet?',
    },
    {
        id: 'best-sellers',
        kind: 'catalogue',
        titleKey: 'ASSISTANT.SUGGESTION.BEST_SELLERS',
        titleFallback: 'My best performing products',
        hintKey: 'ASSISTANT.SUGGESTION.BEST_SELLERS_HINT',
        hintFallback: 'Ranked by what visitors actually do',
        promptKey: 'ASSISTANT.SUGGESTION.BEST_SELLERS_PROMPT',
        promptFallback: 'Which products are performing best this month, and why?',
    },
    {
        id: 'brand-colour',
        kind: 'design',
        titleKey: 'ASSISTANT.SUGGESTION.BRAND_COLOUR',
        titleFallback: 'Change my brand colour',
        hintKey: 'ASSISTANT.SUGGESTION.BRAND_COLOUR_HINT',
        hintFallback: 'Update what customers see',
        promptKey: 'ASSISTANT.SUGGESTION.BRAND_COLOUR_PROMPT',
        promptFallback: 'Show me my current brand colours and help me pick a better primary.',
    },
];

/**
 * Time-of-day greeting.
 *
 * Local to the vendor's browser on purpose: the server's idea of evening is not
 * theirs, and this is the one string on the page that is about them rather than
 * about their shop.
 */
export function greetingKey(now: Date = new Date()): { key: string; fallback: string } {
    const hour = now.getHours();

    if (hour < 12) return { key: 'ASSISTANT.GREETING.MORNING', fallback: 'Good morning.' };
    if (hour < 18) return { key: 'ASSISTANT.GREETING.AFTERNOON', fallback: 'Good afternoon.' };
    return { key: 'ASSISTANT.GREETING.EVENING', fallback: 'Good evening.' };
}
